import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { useProject } from '../context';
import { OnboardingStepPreview } from '../components/OnboardingStepPreview';

/**
 * Platform types for onboarding flow editing.
 */
type PlatformType = 'messaging' | 'desktop';

/**
 * WhatsApp API Limits
 * These constants match the actual limits of WhatsApp Business API
 * to ensure admins cannot configure options that won't work.
 */
const WHATSAPP_LIMITS = {
  // Interactive message button limits
  MAX_BUTTONS: 3,
  MAX_BUTTON_LABEL_LENGTH: 20,
  MAX_BUTTON_ID_LENGTH: 256,

  // List message limits
  MAX_LIST_ITEMS: 10,
  MAX_LIST_ITEM_TITLE_LENGTH: 24,
  MAX_LIST_ITEM_DESCRIPTION_LENGTH: 72,
  MAX_LIST_HEADER_LENGTH: 60,
  MAX_LIST_BUTTON_TEXT_LENGTH: 20,

  // General message limits
  MAX_MESSAGE_LENGTH: 4096,
  MAX_HEADER_TEXT_LENGTH: 60,
  MAX_FOOTER_TEXT_LENGTH: 60,

  // Continue button (informational step)
  MAX_CONTINUE_BUTTON_LENGTH: 20,
} as const;

/**
 * Type for media configuration in onboarding steps.
 */
interface OnboardingMedia {
  type: 'image' | 'video' | 'audio' | 'document';
  source: string;
  caption?: string;
  filename?: string;
  mimeType?: string;
}

/**
 * Type for time picker configuration.
 */
interface TimePickerConfig {
  startHour?: number;
  endHour?: number;
  intervalMinutes?: number;
  format?: '12h' | '24h';
}

/**
 * Type for subscription plan in payment config.
 */
interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency?: string;
  interval: 'trial' | 'weekly' | 'monthly' | 'yearly';
  savingsPercent?: number;
  badge?: string;
  features?: string[];
}

/**
 * Type for payment configuration.
 * Supports external Stripe payment links for regions where in-app payments
 * are not available.
 */
interface PaymentConfig {
  /** Stripe payment link URL for external checkout */
  stripePaymentLink?: string;
  /** Button text for the payment link CTA (default: "Subscribe Now") */
  ctaButtonText?: string;
  /** Optional plans to display for informational purposes */
  plans?: SubscriptionPlan[];
  headerText?: string;
  footerText?: string;
}

/**
 * Type for option in buttons/list steps.
 */
interface StepOption {
  id: string;
  label: string;
}

/**
 * Type for onboarding step configuration.
 * Matches backend onboardingStepSchema.
 */
interface OnboardingStep {
  id: string;
  type: 'text' | 'buttons' | 'list' | 'media' | 'time_picker' | 'payment' | 'informational';
  message: string;
  description?: string;
  field?: string;
  validation?: {
    type: string;
    pattern?: string;
    min?: number;
    max?: number;
  };
  retryMessage?: string;
  next?: string;
  skipIf?: {
    field: string;
    operator: string;
    value?: unknown;
  };
  // Media step configuration
  media?: OnboardingMedia;
  // Button text for informational steps
  continueButtonText?: string;
  // Options for buttons/list steps
  options?: StepOption[];
  // Time picker configuration
  timePickerConfig?: TimePickerConfig;
  // Payment configuration
  paymentConfig?: PaymentConfig;
}

/**
 * Desktop-specific step types that extend the base onboarding step types.
 */
type DesktopStepType =
  | 'text'
  | 'buttons'
  | 'list'
  | 'media'
  | 'time_picker'
  | 'payment'
  | 'informational'
  | 'dropdown'
  | 'date-picker'
  | 'multi-select'
  | 'slider'
  | 'card-selection'
  | 'image'
  | 'rich-text';

/**
 * Configuration for card options in card-selection steps.
 */
interface CardOption {
  id: string;
  title: string;
  description?: string;
  image?: string;
  icon?: string;
  badge?: string;
}

/**
 * Configuration for dropdown steps.
 */
interface DropdownConfig {
  placeholder?: string;
  searchable?: boolean;
}

/**
 * Configuration for date picker steps.
 */
interface DatePickerConfig {
  minDate?: string;
  maxDate?: string;
  format?: string;
  showTime?: boolean;
}

/**
 * Configuration for multi-select steps.
 */
interface MultiSelectConfig {
  minSelections?: number;
  maxSelections?: number;
  layout?: 'grid' | 'list' | 'chips';
}

/**
 * Configuration for slider steps.
 */
interface SliderConfig {
  min: number;
  max: number;
  step?: number;
  showLabels?: boolean;
  minLabel?: string;
  maxLabel?: string;
  defaultValue?: number;
  unit?: string;
}

/**
 * Configuration for card selection steps.
 */
interface CardSelectionConfig {
  layout?: 'grid' | 'carousel';
  columns?: number;
  multiSelect?: boolean;
}

/**
 * Configuration for image steps.
 */
interface ImageStepConfig {
  src: string;
  alt?: string;
  width?: string;
  height?: string;
  fit?: 'cover' | 'contain' | 'fill';
}

/**
 * Configuration for rich text steps.
 */
interface RichTextConfig {
  content: string;
  linksInNewTab?: boolean;
}

/**
 * Type for desktop onboarding step configuration.
 * Extends base onboarding step with desktop-specific options.
 */
interface DesktopOnboardingStep {
  id: string;
  type: DesktopStepType;
  message: string;
  description?: string;
  field?: string;
  validation?: {
    type: string;
    pattern?: string;
    min?: number;
    max?: number;
    minSelections?: number;
    maxSelections?: number;
  };
  // Media step configuration
  media?: OnboardingMedia;
  // Button text for informational steps
  continueButtonText?: string;
  // Options for buttons/list/dropdown/multi-select steps
  options?: StepOption[];
  // Card options for card-selection type
  cardOptions?: CardOption[];
  // Time picker configuration
  timePickerConfig?: TimePickerConfig;
  // Payment configuration
  paymentConfig?: PaymentConfig;
  // Desktop-specific configurations
  dropdownConfig?: DropdownConfig;
  datePickerConfig?: DatePickerConfig;
  multiSelectConfig?: MultiSelectConfig;
  sliderConfig?: SliderConfig;
  cardSelectionConfig?: CardSelectionConfig;
  imageConfig?: ImageStepConfig;
  richTextConfig?: RichTextConfig;
}

/**
 * Type for desktop onboarding flow configuration.
 */
interface DesktopOnboardingFlow {
  steps: DesktopOnboardingStep[];
  startStep?: string;
  showProgressIndicator?: boolean;
  completionMessage: string;
  completionRedirectUrl?: string;
}

/**
 * Type for onboarding flow configuration.
 */
interface OnboardingFlow {
  steps: OnboardingStep[];
  completionMessage: string;
  requireExternalOnboarding?: boolean;
  externalOnboardingMessage?: string;
}

/**
 * Serializes messaging onboarding steps as a linear flow.
 *
 * The admin editor models messaging onboarding as an ordered list, while the
 * runtime follows each step's `next` pointer. Persist a canonical chain so
 * reordering in the editor can't leave stale links behind.
 */
function normalizeMessagingStepsForSave(steps: OnboardingStep[]): OnboardingStep[] {
  return steps.map((step, index) => {
    const nextStepId = steps[index + 1]?.id;
    const normalizedStep: OnboardingStep = {
      ...step,
    };

    if (nextStepId) {
      normalizedStep.next = nextStepId;
    } else {
      delete normalizedStep.next;
    }

    return normalizedStep;
  });
}

/**
 * Form state for editing the onboarding flow (messaging platform).
 */
interface MessagingFormState {
  onboardingFlow: OnboardingFlow;
}

/**
 * Form state for editing the desktop onboarding flow.
 */
interface DesktopFormState {
  desktopOnboardingFlow: DesktopOnboardingFlow;
}

/**
 * Bot Onboarding Edit page component.
 *
 * Dedicated page for configuring the bot's onboarding flow with
 * drag-and-drop ordering, step management, and independent saving.
 */
interface BotOnboardingEditPageProps {
  title?: string;
  embedded?: boolean;
}

export function BotOnboardingEditPage({
  title = 'Onboarding Flow Editor',
  embedded = false,
}: BotOnboardingEditPageProps) {
  const { currentBotId: botId, isLoading: contextLoading } = useProject();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  // Platform toggle state
  const [activePlatform, setActivePlatform] = useState<PlatformType>('messaging');

  // Form state for messaging (WhatsApp/Telegram) onboarding flow
  const [messagingFormState, setMessagingFormState] = useState<MessagingFormState>({
    onboardingFlow: {
      steps: [],
      completionMessage: '',
      requireExternalOnboarding: false,
      externalOnboardingMessage: '',
    },
  });

  // Form state for desktop web app onboarding flow
  const [desktopFormState, setDesktopFormState] = useState<DesktopFormState>({
    desktopOnboardingFlow: {
      steps: [],
      completionMessage: '',
      showProgressIndicator: true,
    },
  });

  // Track if the form has been initialized from data
  const [isInitialized, setIsInitialized] = useState(false);

  // Track if there are unsaved changes per platform
  const [hasMessagingChanges, setHasMessagingChanges] = useState(false);
  const [hasDesktopChanges, setHasDesktopChanges] = useState(false);

  // Drag-and-drop state for onboarding steps
  const [draggedStepIndex, setDraggedStepIndex] = useState<number | null>(null);
  const [dragOverStepIndex, setDragOverStepIndex] = useState<number | null>(null);
  const stepsContainerRef = useRef<HTMLDivElement>(null);

  // Focused step state for preview
  const [focusedStepIndex, setFocusedStepIndex] = useState<number | null>(null);
  const [isCompletionFocused, setIsCompletionFocused] = useState(false);
  const [externalOnboardingError, setExternalOnboardingError] = useState<string | null>(null);

  // Helper to check if any platform has changes
  const hasChanges = activePlatform === 'messaging' ? hasMessagingChanges : hasDesktopChanges;
  const externalOnboardingEnabled = messagingFormState.onboardingFlow.requireExternalOnboarding ?? false;

  // Load bot data
  const botQuery = trpc.bots.get.useQuery(
    { id: botId! },
    { enabled: !!botId }
  );

  // Update mutation for saving onboarding flow independently
  const updateMutation = trpc.bots.update.useMutation({
    onSuccess: () => {
      utils.bots.get.invalidate({ id: botId! });
      utils.bots.list.invalidate();
      // Reset the change flag for the active platform
      if (activePlatform === 'messaging') {
        setHasMessagingChanges(false);
      } else {
        setHasDesktopChanges(false);
      }
    },
  });

  // Initialize form state when bot data loads
  useEffect(() => {
    if (botQuery.data && !isInitialized) {
      // Initialize messaging (WhatsApp/Telegram) flow
      const onboardingFlow =
        botQuery.data.onboardingFlow &&
        typeof botQuery.data.onboardingFlow === 'object'
          ? (botQuery.data.onboardingFlow as OnboardingFlow)
          : { steps: [], completionMessage: '', requireExternalOnboarding: false, externalOnboardingMessage: '' };
      setMessagingFormState({
        onboardingFlow: {
          requireExternalOnboarding: false,
          externalOnboardingMessage: '',
          ...onboardingFlow,
        },
      });

      // Initialize desktop flow
      const desktopOnboardingFlow =
        botQuery.data.desktopOnboardingFlow &&
        typeof botQuery.data.desktopOnboardingFlow === 'object'
          ? (botQuery.data.desktopOnboardingFlow as DesktopOnboardingFlow)
          : { steps: [], completionMessage: '', showProgressIndicator: true };
      setDesktopFormState({
        desktopOnboardingFlow,
      });

      setIsInitialized(true);
    }
  }, [botQuery.data, isInitialized]);

  /**
   * Generate a unique ID for new onboarding steps.
   */
  const generateStepId = () => {
    return `step_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  /**
   * Mark form as changed for the active platform.
   */
  const markAsChanged = () => {
    if (activePlatform === 'messaging') {
      setHasMessagingChanges(true);
    } else {
      setHasDesktopChanges(true);
    }
  };

  /**
   * Get current steps based on active platform.
   */
  const getCurrentSteps = () => {
    return activePlatform === 'messaging'
      ? messagingFormState.onboardingFlow.steps
      : desktopFormState.desktopOnboardingFlow.steps;
  };

  /**
   * Get current completion message based on active platform.
   */
  const getCurrentCompletionMessage = () => {
    return activePlatform === 'messaging'
      ? messagingFormState.onboardingFlow.completionMessage
      : desktopFormState.desktopOnboardingFlow.completionMessage;
  };

  /**
   * Handle platform tab switch.
   */
  const handlePlatformSwitch = (platform: PlatformType) => {
    setActivePlatform(platform);
    // Reset focus when switching platforms
    setFocusedStepIndex(null);
    setIsCompletionFocused(false);
  };

  /**
   * Copy flow from one platform to another.
   */
  const handleCopyFlow = (direction: 'messagingToDesktop' | 'desktopToMessaging') => {
    if (direction === 'messagingToDesktop') {
      // Convert messaging steps to desktop format
      const convertedSteps: DesktopOnboardingStep[] = messagingFormState.onboardingFlow.steps.map((step) => ({
        ...step,
        type: step.type as DesktopStepType,
      }));
      setDesktopFormState({
        desktopOnboardingFlow: {
          ...desktopFormState.desktopOnboardingFlow,
          steps: convertedSteps,
          completionMessage: messagingFormState.onboardingFlow.completionMessage,
        },
      });
      setHasDesktopChanges(true);
    } else {
      // Convert desktop steps to messaging format (only compatible types)
      const messagingCompatibleTypes = ['text', 'buttons', 'list', 'media', 'time_picker', 'payment', 'informational'];
      const convertedSteps: OnboardingStep[] = desktopFormState.desktopOnboardingFlow.steps
        .filter((step) => messagingCompatibleTypes.includes(step.type))
        .map((step) => ({
          id: step.id,
          type: step.type as OnboardingStep['type'],
          message: step.message,
          field: step.field,
          media: step.media,
          continueButtonText: step.continueButtonText,
          options: step.options,
          timePickerConfig: step.timePickerConfig,
          paymentConfig: step.paymentConfig,
        }));
      setMessagingFormState({
        onboardingFlow: {
          ...messagingFormState.onboardingFlow,
          steps: convertedSteps,
          completionMessage: desktopFormState.desktopOnboardingFlow.completionMessage,
        },
      });
      setHasMessagingChanges(true);
    }
  };

  /**
   * Add a new step to the onboarding flow.
   */
  const handleAddStep = () => {
    if (activePlatform === 'messaging') {
      const newStep: OnboardingStep = {
        id: generateStepId(),
        type: 'text',
        message: '',
      };
      setMessagingFormState((prev) => ({
        ...prev,
        onboardingFlow: {
          ...prev.onboardingFlow,
          steps: [...prev.onboardingFlow.steps, newStep],
        },
      }));
    } else {
      const newStep: DesktopOnboardingStep = {
        id: generateStepId(),
        type: 'text',
        message: '',
      };
      setDesktopFormState((prev) => ({
        ...prev,
        desktopOnboardingFlow: {
          ...prev.desktopOnboardingFlow,
          steps: [...prev.desktopOnboardingFlow.steps, newStep],
        },
      }));
    }
    markAsChanged();
  };

  /**
   * Remove a step from the onboarding flow.
   */
  const handleRemoveStep = (index: number) => {
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => ({
        ...prev,
        onboardingFlow: {
          ...prev.onboardingFlow,
          steps: prev.onboardingFlow.steps.filter((_, i) => i !== index),
        },
      }));
    } else {
      setDesktopFormState((prev) => ({
        ...prev,
        desktopOnboardingFlow: {
          ...prev.desktopOnboardingFlow,
          steps: prev.desktopOnboardingFlow.steps.filter((_, i) => i !== index),
        },
      }));
    }
    markAsChanged();
  };

  /**
   * Move a step up in the onboarding flow.
   */
  const handleMoveStepUp = (index: number) => {
    if (index === 0) return;
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => {
        const newSteps = [...prev.onboardingFlow.steps];
        [newSteps[index - 1], newSteps[index]] = [newSteps[index], newSteps[index - 1]];
        return {
          ...prev,
          onboardingFlow: {
            ...prev.onboardingFlow,
            steps: newSteps,
          },
        };
      });
    } else {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        [newSteps[index - 1], newSteps[index]] = [newSteps[index], newSteps[index - 1]];
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
    }
    markAsChanged();
  };

  /**
   * Move a step down in the onboarding flow.
   */
  const handleMoveStepDown = (index: number) => {
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => {
        if (index === prev.onboardingFlow.steps.length - 1) return prev;
        const newSteps = [...prev.onboardingFlow.steps];
        [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
        return {
          ...prev,
          onboardingFlow: {
            ...prev.onboardingFlow,
            steps: newSteps,
          },
        };
      });
    } else {
      setDesktopFormState((prev) => {
        if (index === prev.desktopOnboardingFlow.steps.length - 1) return prev;
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
    }
    markAsChanged();
  };

  /**
   * Handle drag start for onboarding step reordering.
   */
  const handleStepDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDraggedStepIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    // Add visual feedback by setting opacity on the dragged element
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  /**
   * Handle drag end for onboarding step reordering.
   */
  const handleStepDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    setDraggedStepIndex(null);
    setDragOverStepIndex(null);
    // Reset opacity
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '1';
    }
  };

  /**
   * Handle drag over for onboarding step reordering.
   */
  const handleStepDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedStepIndex !== null && draggedStepIndex !== index) {
      setDragOverStepIndex(index);
    }
  };

  /**
   * Handle drag leave for onboarding step reordering.
   */
  const handleStepDragLeave = () => {
    setDragOverStepIndex(null);
  };

  /**
   * Handle drop for onboarding step reordering.
   */
  const handleStepDrop = (e: React.DragEvent<HTMLDivElement>, targetIndex: number) => {
    e.preventDefault();
    const sourceIndex = draggedStepIndex;

    if (sourceIndex === null || sourceIndex === targetIndex) {
      setDraggedStepIndex(null);
      setDragOverStepIndex(null);
      return;
    }

    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => {
        const newSteps = [...prev.onboardingFlow.steps];
        // Remove the dragged item and insert it at the target position
        const [draggedItem] = newSteps.splice(sourceIndex, 1);
        newSteps.splice(targetIndex, 0, draggedItem);
        return {
          ...prev,
          onboardingFlow: {
            ...prev.onboardingFlow,
            steps: newSteps,
          },
        };
      });
    } else {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        // Remove the dragged item and insert it at the target position
        const [draggedItem] = newSteps.splice(sourceIndex, 1);
        newSteps.splice(targetIndex, 0, draggedItem);
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
    }

    setDraggedStepIndex(null);
    setDragOverStepIndex(null);
    markAsChanged();
  };

  /**
   * Update an onboarding step field.
   */
  const handleUpdateStep = (
    index: number,
    field: keyof OnboardingStep | keyof DesktopOnboardingStep,
    value: string
  ) => {
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => {
        const newSteps = [...prev.onboardingFlow.steps];
        newSteps[index] = { ...newSteps[index], [field]: value };
        return {
          ...prev,
          onboardingFlow: {
            ...prev.onboardingFlow,
            steps: newSteps,
          },
        };
      });
    } else {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        newSteps[index] = { ...newSteps[index], [field]: value };
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
    }
    markAsChanged();
  };

  /**
   * Update the media source URL for a media step.
   */
  const handleUpdateStepMediaUrl = (index: number, url: string) => {
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => {
        const newSteps = [...prev.onboardingFlow.steps];
        const step = newSteps[index];
        newSteps[index] = {
          ...step,
          media: {
            type: step.media?.type || 'image',
            source: url,
            caption: step.media?.caption,
            filename: step.media?.filename,
            mimeType: step.media?.mimeType,
          },
        };
        return {
          ...prev,
          onboardingFlow: {
            ...prev.onboardingFlow,
            steps: newSteps,
          },
        };
      });
    } else {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        const step = newSteps[index];
        newSteps[index] = {
          ...step,
          media: {
            type: step.media?.type || 'image',
            source: url,
            caption: step.media?.caption,
            filename: step.media?.filename,
            mimeType: step.media?.mimeType,
          },
        };
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
    }
    markAsChanged();
  };

  /**
   * Update the media type for a media step.
   */
  const handleUpdateStepMediaType = (index: number, mediaType: OnboardingMedia['type']) => {
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => {
        const newSteps = [...prev.onboardingFlow.steps];
        const step = newSteps[index];
        newSteps[index] = {
          ...step,
          media: {
            type: mediaType,
            source: step.media?.source || '',
            caption: step.media?.caption,
            filename: step.media?.filename,
            mimeType: step.media?.mimeType,
          },
        };
        return {
          ...prev,
          onboardingFlow: {
            ...prev.onboardingFlow,
            steps: newSteps,
          },
        };
      });
    } else {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        const step = newSteps[index];
        newSteps[index] = {
          ...step,
          media: {
            type: mediaType,
            source: step.media?.source || '',
            caption: step.media?.caption,
            filename: step.media?.filename,
            mimeType: step.media?.mimeType,
          },
        };
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
    }
    markAsChanged();
  };

  /**
   * Update the media caption for a media step.
   */
  const handleUpdateStepMediaCaption = (index: number, caption: string) => {
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => {
        const newSteps = [...prev.onboardingFlow.steps];
        const step = newSteps[index];
        newSteps[index] = {
          ...step,
          media: {
            type: step.media?.type || 'image',
            source: step.media?.source || '',
            caption: caption || undefined,
            filename: step.media?.filename,
            mimeType: step.media?.mimeType,
          },
        };
        return {
          ...prev,
          onboardingFlow: {
            ...prev.onboardingFlow,
            steps: newSteps,
          },
        };
      });
    } else {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        const step = newSteps[index];
        newSteps[index] = {
          ...step,
          media: {
            type: step.media?.type || 'image',
            source: step.media?.source || '',
            caption: caption || undefined,
            filename: step.media?.filename,
            mimeType: step.media?.mimeType,
          },
        };
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
    }
    markAsChanged();
  };

  /**
   * Update the media filename for a media step.
   */
  const handleUpdateStepMediaFilename = (index: number, filename: string) => {
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => {
        const newSteps = [...prev.onboardingFlow.steps];
        const step = newSteps[index];
        newSteps[index] = {
          ...step,
          media: {
            type: step.media?.type || 'document',
            source: step.media?.source || '',
            caption: step.media?.caption,
            filename: filename || undefined,
            mimeType: step.media?.mimeType,
          },
        };
        return {
          ...prev,
          onboardingFlow: {
            ...prev.onboardingFlow,
            steps: newSteps,
          },
        };
      });
    } else {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        const step = newSteps[index];
        newSteps[index] = {
          ...step,
          media: {
            type: step.media?.type || 'document',
            source: step.media?.source || '',
            caption: step.media?.caption,
            filename: filename || undefined,
            mimeType: step.media?.mimeType,
          },
        };
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
    }
    markAsChanged();
  };

  /**
   * Get the maximum number of options allowed based on step type.
   * WhatsApp has different limits for buttons (3) vs lists (10).
   */
  const getMaxOptionsForStepType = (stepType: string): number => {
    return stepType === 'buttons' ? WHATSAPP_LIMITS.MAX_BUTTONS : WHATSAPP_LIMITS.MAX_LIST_ITEMS;
  };

  /**
   * Get the maximum label length allowed based on step type.
   */
  const getMaxLabelLengthForStepType = (stepType: string): number => {
    return stepType === 'buttons' ? WHATSAPP_LIMITS.MAX_BUTTON_LABEL_LENGTH : WHATSAPP_LIMITS.MAX_LIST_ITEM_TITLE_LENGTH;
  };

  /**
   * Add an option to a buttons/list step.
   * Enforces WhatsApp limits: max 3 buttons or max 10 list items (for messaging only).
   */
  const handleAddOption = (index: number) => {
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => {
        const newSteps = [...prev.onboardingFlow.steps];
        const step = newSteps[index];
        const currentOptions = step.options || [];
        const maxOptions = getMaxOptionsForStepType(step.type);

        // Enforce WhatsApp limit
        if (currentOptions.length >= maxOptions) {
          return prev;
        }

        const newOption: StepOption = {
          id: `option_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
          label: '',
        };
        newSteps[index] = {
          ...step,
          options: [...currentOptions, newOption],
        };
        return {
          ...prev,
          onboardingFlow: {
            ...prev.onboardingFlow,
            steps: newSteps,
          },
        };
      });
    } else {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        const step = newSteps[index];
        const currentOptions = step.options || [];

        // No strict limit for desktop, but reasonable max of 50
        if (currentOptions.length >= 50) {
          return prev;
        }

        const newOption: StepOption = {
          id: `option_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
          label: '',
        };
        newSteps[index] = {
          ...step,
          options: [...currentOptions, newOption],
        };
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
    }
    markAsChanged();
  };

  /**
   * Remove an option from a buttons/list step.
   */
  const handleRemoveOption = (stepIndex: number, optionIndex: number) => {
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => {
        const newSteps = [...prev.onboardingFlow.steps];
        const step = newSteps[stepIndex];
        newSteps[stepIndex] = {
          ...step,
          options: step.options?.filter((_, i) => i !== optionIndex),
        };
        return {
          ...prev,
          onboardingFlow: {
            ...prev.onboardingFlow,
            steps: newSteps,
          },
        };
      });
    } else {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        const step = newSteps[stepIndex];
        newSteps[stepIndex] = {
          ...step,
          options: step.options?.filter((_, i) => i !== optionIndex),
        };
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
    }
    markAsChanged();
  };

  /**
   * Update an option in a buttons/list step.
   */
  const handleUpdateOption = (stepIndex: number, optionIndex: number, field: keyof StepOption, value: string) => {
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => {
        const newSteps = [...prev.onboardingFlow.steps];
        const step = newSteps[stepIndex];
        const newOptions = [...(step.options || [])];
        newOptions[optionIndex] = { ...newOptions[optionIndex], [field]: value };
        newSteps[stepIndex] = {
          ...step,
          options: newOptions,
        };
        return {
          ...prev,
          onboardingFlow: {
            ...prev.onboardingFlow,
            steps: newSteps,
          },
        };
      });
    } else {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        const step = newSteps[stepIndex];
        const newOptions = [...(step.options || [])];
        newOptions[optionIndex] = { ...newOptions[optionIndex], [field]: value };
        newSteps[stepIndex] = {
          ...step,
          options: newOptions,
        };
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
    }
    markAsChanged();
  };

  /**
   * Update time picker config for a time_picker step.
   */
  const handleUpdateTimePickerConfig = (index: number, field: keyof TimePickerConfig, value: number | string) => {
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => {
        const newSteps = [...prev.onboardingFlow.steps];
        const step = newSteps[index];
        newSteps[index] = {
          ...step,
          timePickerConfig: {
            ...step.timePickerConfig,
            [field]: value,
          },
        };
        return {
          ...prev,
          onboardingFlow: {
            ...prev.onboardingFlow,
            steps: newSteps,
          },
        };
      });
    } else {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        const step = newSteps[index];
        newSteps[index] = {
          ...step,
          timePickerConfig: {
            ...step.timePickerConfig,
            [field]: value,
          },
        };
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
    }
    markAsChanged();
  };

  /**
   * Update date picker config for a date-picker step.
   * Desktop-only step type.
   */
  const handleUpdateDatePickerConfig = (index: number, field: keyof DatePickerConfig, value: string | boolean) => {
    if (activePlatform === 'desktop') {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        const step = newSteps[index];
        newSteps[index] = {
          ...step,
          datePickerConfig: {
            ...step.datePickerConfig,
            [field]: value,
          },
        };
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
      markAsChanged();
    }
  };

  /**
   * Update multi-select config for a multi-select step.
   * Desktop-only step type.
   */
  const handleUpdateMultiSelectConfig = (index: number, field: keyof MultiSelectConfig, value: number | string | undefined) => {
    if (activePlatform === 'desktop') {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        const step = newSteps[index];
        newSteps[index] = {
          ...step,
          multiSelectConfig: {
            ...step.multiSelectConfig,
            [field]: value,
          },
        };
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
      markAsChanged();
    }
  };

  /**
   * Update dropdown config for a dropdown step.
   * Desktop-only step type.
   */
  const handleUpdateDropdownConfig = (index: number, field: keyof DropdownConfig, value: string | boolean) => {
    if (activePlatform === 'desktop') {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        const step = newSteps[index];
        newSteps[index] = {
          ...step,
          dropdownConfig: {
            ...step.dropdownConfig,
            [field]: value,
          },
        };
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
      markAsChanged();
    }
  };

  /**
   * Add a plan to payment config.
   * Enforces WhatsApp limit: max 10 list items (plans) for messaging only.
   */
  const handleAddPaymentPlan = (index: number) => {
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => {
        const newSteps = [...prev.onboardingFlow.steps];
        const step = newSteps[index];
        const currentPlans = step.paymentConfig?.plans || [];

        // Enforce WhatsApp limit
        if (currentPlans.length >= WHATSAPP_LIMITS.MAX_LIST_ITEMS) {
          return prev;
        }

        const newPlan: SubscriptionPlan = {
          id: `plan_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
          name: '',
          price: 0,
          currency: 'USD',
          interval: 'monthly',
        };
        newSteps[index] = {
          ...step,
          paymentConfig: {
            ...step.paymentConfig,
            plans: [...currentPlans, newPlan],
          },
        };
        return {
          ...prev,
          onboardingFlow: {
            ...prev.onboardingFlow,
            steps: newSteps,
          },
        };
      });
    } else {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        const step = newSteps[index];
        const currentPlans = step.paymentConfig?.plans || [];

        // No strict limit for desktop
        const newPlan: SubscriptionPlan = {
          id: `plan_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
          name: '',
          price: 0,
          currency: 'USD',
          interval: 'monthly',
        };
        newSteps[index] = {
          ...step,
          paymentConfig: {
            ...step.paymentConfig,
            plans: [...currentPlans, newPlan],
          },
        };
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
    }
    markAsChanged();
  };

  /**
   * Remove a plan from payment config.
   */
  const handleRemovePaymentPlan = (stepIndex: number, planIndex: number) => {
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => {
        const newSteps = [...prev.onboardingFlow.steps];
        const step = newSteps[stepIndex];
        newSteps[stepIndex] = {
          ...step,
          paymentConfig: {
            ...step.paymentConfig,
            plans: step.paymentConfig?.plans?.filter((_, i) => i !== planIndex) || [],
          },
        };
        return {
          ...prev,
          onboardingFlow: {
            ...prev.onboardingFlow,
            steps: newSteps,
          },
        };
      });
    } else {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        const step = newSteps[stepIndex];
        newSteps[stepIndex] = {
          ...step,
          paymentConfig: {
            ...step.paymentConfig,
            plans: step.paymentConfig?.plans?.filter((_, i) => i !== planIndex) || [],
          },
        };
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
    }
    markAsChanged();
  };

  /**
   * Update a plan in payment config.
   */
  const handleUpdatePaymentPlan = (
    stepIndex: number,
    planIndex: number,
    field: keyof SubscriptionPlan,
    value: string | number
  ) => {
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => {
        const newSteps = [...prev.onboardingFlow.steps];
        const step = newSteps[stepIndex];
        const newPlans = [...(step.paymentConfig?.plans || [])];
        newPlans[planIndex] = { ...newPlans[planIndex], [field]: value };
        newSteps[stepIndex] = {
          ...step,
          paymentConfig: {
            ...step.paymentConfig,
            plans: newPlans,
          },
        };
        return {
          ...prev,
          onboardingFlow: {
            ...prev.onboardingFlow,
            steps: newSteps,
          },
        };
      });
    } else {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        const step = newSteps[stepIndex];
        const newPlans = [...(step.paymentConfig?.plans || [])];
        newPlans[planIndex] = { ...newPlans[planIndex], [field]: value };
        newSteps[stepIndex] = {
          ...step,
          paymentConfig: {
            ...step.paymentConfig,
            plans: newPlans,
          },
        };
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
    }
    markAsChanged();
  };

  /**
   * Update payment config header/footer text.
   */
  const handleUpdatePaymentConfigText = (index: number, field: 'headerText' | 'footerText', value: string) => {
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => {
        const newSteps = [...prev.onboardingFlow.steps];
        const step = newSteps[index];
        newSteps[index] = {
          ...step,
          paymentConfig: {
            ...step.paymentConfig,
            [field]: value || undefined,
          },
        };
        return {
          ...prev,
          onboardingFlow: {
            ...prev.onboardingFlow,
            steps: newSteps,
          },
        };
      });
    } else {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        const step = newSteps[index];
        newSteps[index] = {
          ...step,
          paymentConfig: {
            ...step.paymentConfig,
            [field]: value || undefined,
          },
        };
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
    }
    markAsChanged();
  };

  /**
   * Update payment config field (stripePaymentLink, ctaButtonText).
   */
  const handleUpdatePaymentConfigField = (index: number, field: 'stripePaymentLink' | 'ctaButtonText', value: string) => {
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => {
        const newSteps = [...prev.onboardingFlow.steps];
        const step = newSteps[index];
        newSteps[index] = {
          ...step,
          paymentConfig: {
            ...step.paymentConfig,
            [field]: value || undefined,
          },
        };
        return {
          ...prev,
          onboardingFlow: {
            ...prev.onboardingFlow,
            steps: newSteps,
          },
        };
      });
    } else {
      setDesktopFormState((prev) => {
        const newSteps = [...prev.desktopOnboardingFlow.steps];
        const step = newSteps[index];
        newSteps[index] = {
          ...step,
          paymentConfig: {
            ...step.paymentConfig,
            [field]: value || undefined,
          },
        };
        return {
          ...prev,
          desktopOnboardingFlow: {
            ...prev.desktopOnboardingFlow,
            steps: newSteps,
          },
        };
      });
    }
    markAsChanged();
  };

  /**
   * Update the onboarding completion message.
   */
  const handleUpdateCompletionMessage = (value: string) => {
    if (activePlatform === 'messaging') {
      setMessagingFormState((prev) => ({
        ...prev,
        onboardingFlow: {
          ...prev.onboardingFlow,
          completionMessage: value,
        },
      }));
    } else {
      setDesktopFormState((prev) => ({
        ...prev,
        desktopOnboardingFlow: {
          ...prev.desktopOnboardingFlow,
          completionMessage: value,
        },
      }));
    }
    markAsChanged();
  };

  const handleUpdateRequireExternalOnboarding = (value: boolean) => {
    if (!value) {
      setExternalOnboardingError(null);
    }
    setMessagingFormState((prev) => ({
      ...prev,
      onboardingFlow: {
        ...prev.onboardingFlow,
        requireExternalOnboarding: value,
      },
    }));
    markAsChanged();
  };

  const handleUpdateExternalOnboardingMessage = (value: string) => {
    if (value.trim()) {
      setExternalOnboardingError(null);
    }
    setMessagingFormState((prev) => ({
      ...prev,
      onboardingFlow: {
        ...prev.onboardingFlow,
        externalOnboardingMessage: value,
      },
    }));
    markAsChanged();
  };

  /**
   * Handle focusing on a step for preview.
   */
  const handleFocusStep = (index: number) => {
    setFocusedStepIndex(index);
    setIsCompletionFocused(false);
  };

  /**
   * Handle focusing on the completion message for preview.
   */
  const handleFocusCompletion = () => {
    setFocusedStepIndex(null);
    setIsCompletionFocused(true);
  };

  /**
   * Handle form submission - saves the onboarding flow for the active platform.
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!botId) return;

    if (activePlatform === 'messaging') {
      if (
        messagingFormState.onboardingFlow.requireExternalOnboarding &&
        !messagingFormState.onboardingFlow.externalOnboardingMessage?.trim()
      ) {
        setExternalOnboardingError('External onboarding message is required when external onboarding is enabled.');
        return;
      }

      setExternalOnboardingError(null);
      const hasMessagingOnboardingConfig =
        messagingFormState.onboardingFlow.steps.length > 0 ||
        Boolean(messagingFormState.onboardingFlow.completionMessage?.trim()) ||
        Boolean(messagingFormState.onboardingFlow.requireExternalOnboarding) ||
        Boolean(messagingFormState.onboardingFlow.externalOnboardingMessage?.trim());

      const normalizedOnboardingFlow: OnboardingFlow = {
        ...messagingFormState.onboardingFlow,
        steps: normalizeMessagingStepsForSave(messagingFormState.onboardingFlow.steps),
      };

      updateMutation.mutate({
        id: botId,
        onboardingFlow: hasMessagingOnboardingConfig
          ? normalizedOnboardingFlow
          : undefined,
      });
    } else {
      updateMutation.mutate({
        id: botId,
        desktopOnboardingFlow: desktopFormState.desktopOnboardingFlow.steps.length > 0
          ? desktopFormState.desktopOnboardingFlow
          : null,
      });
    }
  };

  // Handle context loading
  if (contextLoading) {
    return <div style={styles.loading}>Loading...</div>;
  }

  // Handle no bot selected
  if (!botId) {
    return (
      <div style={styles.error}>
        No bot selected. Please select a bot from the sidebar.
      </div>
    );
  }

  // Loading state
  if (botQuery.isLoading) {
    return <div style={styles.loading}>Loading bot...</div>;
  }

  // Error state
  if (botQuery.error) {
    return (
      <div style={styles.error}>
        Error loading bot: {botQuery.error.message}
      </div>
    );
  }

  // Bot not found
  if (!botQuery.data) {
    return (
      <div style={styles.error}>
        Bot not found
      </div>
    );
  }

  // Get the current steps based on active platform
  const currentSteps = getCurrentSteps();
  const currentCompletionMessage = getCurrentCompletionMessage();

  // Get the focused step for preview
  const focusedStep = focusedStepIndex !== null
    ? currentSteps[focusedStepIndex]
    : null;

  return (
    <div style={styles.pageContainer}>
      <div style={styles.header}>
        {!embedded && (
          <button onClick={() => navigate('/bot/details')} style={styles.backButton}>
            &larr; Back to Bot Details
          </button>
        )}
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            {!embedded && <h1 style={styles.title}>{title}</h1>}
            <span style={styles.botName}>{botQuery.data.name}</span>
          </div>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={updateMutation.isPending || !hasChanges}
            style={{
              ...styles.saveButton,
              ...(updateMutation.isPending || !hasChanges ? styles.buttonDisabled : {}),
            }}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Platform Toggle Tabs */}
        <div style={styles.platformToggle}>
          <div style={styles.platformTabs}>
            <button
              onClick={() => handlePlatformSwitch('messaging')}
              style={{
                ...styles.platformTab,
                ...(activePlatform === 'messaging' ? styles.platformTabActive : {}),
              }}
            >
              <span style={styles.platformTabIcon}>
                {activePlatform === 'messaging' ? '📱' : '📱'}
              </span>
              WhatsApp/Telegram
              {hasMessagingChanges && <span style={styles.unsavedDot} title="Unsaved changes" />}
            </button>
            <button
              onClick={() => handlePlatformSwitch('desktop')}
              style={{
                ...styles.platformTab,
                ...(activePlatform === 'desktop' ? styles.platformTabActive : {}),
              }}
            >
              <span style={styles.platformTabIcon}>
                {activePlatform === 'desktop' ? '🖥️' : '🖥️'}
              </span>
              Desktop Web App
              {hasDesktopChanges && <span style={styles.unsavedDot} title="Unsaved changes" />}
            </button>
          </div>
          <div style={styles.copyFlowContainer}>
            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleCopyFlow(e.target.value as 'messagingToDesktop' | 'desktopToMessaging');
                  e.target.value = '';
                }
              }}
              style={styles.copyFlowSelect}
              value=""
            >
              <option value="">Copy Flow...</option>
              <option value="messagingToDesktop">Copy Messaging → Desktop</option>
              <option value="desktopToMessaging">Copy Desktop → Messaging (compatible only)</option>
            </select>
          </div>
        </div>
      </div>

      <div style={styles.splitPane}>
        {/* Left Pane - Editor */}
        <div style={styles.editorPane}>
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>
                {activePlatform === 'messaging' ? 'WhatsApp/Telegram' : 'Desktop Web App'} Onboarding Steps
              </h2>
              <p style={styles.cardDescription}>
                {activePlatform === 'messaging'
                  ? 'Configure the onboarding steps for WhatsApp and Telegram users. These steps are constrained by messaging platform limits.'
                  : 'Configure the onboarding steps for Desktop Web App users. You have access to rich web components and extended step types.'}
                {' '}Click on a step to preview it. Drag steps to reorder them.
              </p>
              {activePlatform === 'messaging' && (
                <div style={styles.platformHint}>
                  <span style={styles.platformHintIcon}>ℹ️</span>
                  <span>WhatsApp limits: max 3 buttons, max 10 list items, max 20 characters for button labels.</span>
                </div>
              )}
              {activePlatform === 'desktop' && (
                <div style={styles.platformHintDesktop}>
                  <span style={styles.platformHintIcon}>✨</span>
                  <span>Desktop mode: Additional step types available including dropdown, date picker, multi-select, slider, and card selection.</span>
                </div>
              )}

              {updateMutation.error && (
                <div style={styles.errorMessage}>
                  Error updating onboarding flow: {updateMutation.error.message}
                </div>
              )}

              {updateMutation.isSuccess && !hasChanges && (
                <div style={styles.successMessage}>
                  Onboarding flow saved successfully!
                </div>
              )}

              {/* Steps List */}
              <div ref={stepsContainerRef} style={styles.stepsContainer}>
                {currentSteps.length === 0 ? (
                  <div style={styles.emptyState}>
                    <div style={styles.emptyStateIcon}>📋</div>
                    <p style={styles.emptyStateText}>No onboarding steps yet</p>
                    <p style={styles.emptyStateHint}>
                      Click "Add Step" below to create your first onboarding step
                    </p>
                  </div>
                ) : (
                  currentSteps.map((step, index) => (
                    <div
                      key={step.id}
                      draggable
                      onClick={() => handleFocusStep(index)}
                      onDragStart={(e) => handleStepDragStart(e, index)}
                      onDragEnd={handleStepDragEnd}
                      onDragOver={(e) => handleStepDragOver(e, index)}
                      onDragLeave={handleStepDragLeave}
                      onDrop={(e) => handleStepDrop(e, index)}
                      style={{
                        ...styles.stepCard,
                        ...(focusedStepIndex === index ? styles.stepCardFocused : {}),
                        ...(draggedStepIndex === index ? styles.stepCardDragging : {}),
                        ...(dragOverStepIndex === index ? styles.stepCardDragOver : {}),
                      }}
                    >
                      <div style={styles.stepCardHeader}>
                        <span style={styles.dragHandle} title="Drag to reorder">⋮⋮</span>
                        <span style={styles.stepNumber}>{index + 1}</span>
                        <select
                          value={step.type}
                          onChange={(e) => {
                            handleUpdateStep(
                              index,
                              'type',
                              e.target.value
                            );
                            handleFocusStep(index);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={styles.stepTypeSelect}
                        >
                          <option value="text">Text</option>
                          <option value="buttons">Buttons</option>
                          <option value="list">List</option>
                          <option value="media">Media</option>
                          <option value="time_picker">Time Picker</option>
                          <option value="payment">Payment</option>
                          <option value="informational">Informational</option>
                          {activePlatform === 'desktop' && (
                            <>
                              <option disabled>─── Desktop Only ───</option>
                              <option value="dropdown">Dropdown</option>
                              <option value="date-picker">Date Picker</option>
                              <option value="multi-select">Multi-Select</option>
                              <option value="slider">Slider</option>
                              <option value="card-selection">Card Selection</option>
                              <option value="image">Image</option>
                              <option value="rich-text">Rich Text</option>
                            </>
                          )}
                        </select>
                        <div style={styles.stepFieldContainer}>
                          <span style={styles.stepFieldLabel}>Field:</span>
                          <input
                            type="text"
                            value={step.field || ''}
                            onChange={(e) => handleUpdateStep(index, 'field', e.target.value)}
                            onFocus={() => handleFocusStep(index)}
                            onClick={(e) => e.stopPropagation()}
                            style={styles.stepFieldInput}
                            placeholder="fieldName"
                          />
                        </div>
                        <div style={styles.stepActions}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveStepUp(index);
                            }}
                            disabled={index === 0}
                            style={{
                              ...styles.stepActionButton,
                              ...(index === 0 ? styles.stepActionButtonDisabled : {}),
                            }}
                            title="Move Up"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveStepDown(index);
                            }}
                            disabled={index === currentSteps.length - 1}
                            style={{
                              ...styles.stepActionButton,
                              ...(index === currentSteps.length - 1
                                ? styles.stepActionButtonDisabled
                                : {}),
                            }}
                            title="Move Down"
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveStep(index);
                              // Clear focus if removing the focused step
                              if (focusedStepIndex === index) {
                                setFocusedStepIndex(null);
                              } else if (focusedStepIndex !== null && focusedStepIndex > index) {
                                setFocusedStepIndex(focusedStepIndex - 1);
                              }
                            }}
                            style={styles.stepRemoveButton}
                            title="Remove Step"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <textarea
                        value={step.message}
                        onChange={(e) => handleUpdateStep(index, 'message', e.target.value)}
                        onFocus={() => handleFocusStep(index)}
                        onClick={(e) => e.stopPropagation()}
                        style={styles.stepMessageInput}
                        rows={3}
                        placeholder="Enter the step message..."
                      />
                      {step.type === 'media' && (
                        <div style={styles.configSection}>
                          <label style={styles.configSectionLabel}>Media Configuration</label>
                          <div style={styles.configRow}>
                            <div style={styles.configField}>
                              <label style={styles.configFieldLabel}>Media Type</label>
                              <select
                                value={step.media?.type || 'image'}
                                onChange={(e) => handleUpdateStepMediaType(index, e.target.value as OnboardingMedia['type'])}
                                onFocus={() => handleFocusStep(index)}
                                onClick={(e) => e.stopPropagation()}
                                style={styles.configSelect}
                              >
                                <option value="image">Image</option>
                                <option value="video">Video</option>
                                <option value="audio">Audio</option>
                                <option value="document">Document</option>
                              </select>
                            </div>
                          </div>
                          <div style={styles.configField}>
                            <label style={styles.configFieldLabel}>Media URL</label>
                            <input
                              type="text"
                              value={step.media?.source || ''}
                              onChange={(e) => handleUpdateStepMediaUrl(index, e.target.value)}
                              onFocus={() => handleFocusStep(index)}
                              onClick={(e) => e.stopPropagation()}
                              style={styles.configInput}
                              placeholder="Enter the media URL..."
                            />
                          </div>
                          <div style={styles.configField}>
                            <label style={styles.configFieldLabel}>Caption (optional)</label>
                            <input
                              type="text"
                              value={step.media?.caption || ''}
                              onChange={(e) => handleUpdateStepMediaCaption(index, e.target.value)}
                              onFocus={() => handleFocusStep(index)}
                              onClick={(e) => e.stopPropagation()}
                              style={styles.configInput}
                              placeholder="Enter caption text..."
                            />
                          </div>
                          {step.media?.type === 'document' && (
                            <div style={styles.configField}>
                              <label style={styles.configFieldLabel}>Filename (optional)</label>
                              <input
                                type="text"
                                value={step.media?.filename || ''}
                                onChange={(e) => handleUpdateStepMediaFilename(index, e.target.value)}
                                onFocus={() => handleFocusStep(index)}
                                onClick={(e) => e.stopPropagation()}
                                style={styles.configInput}
                                placeholder="e.g., document.pdf"
                              />
                            </div>
                          )}
                        </div>
                      )}
                      {(step.type === 'buttons' || step.type === 'list') && (
                        <div style={styles.configSection}>
                          <div style={styles.configSectionHeader}>
                            <label style={styles.configSectionLabel}>
                              {step.type === 'buttons' ? 'Button Options' : 'List Options'}
                            </label>
                            <span style={styles.limitIndicator}>
                              {step.options?.length || 0}/{getMaxOptionsForStepType(step.type)} max
                            </span>
                          </div>
                          <div style={styles.optionsList}>
                            {(step.options || []).map((option, optionIndex) => {
                              const maxLabelLength = getMaxLabelLengthForStepType(step.type);
                              const labelLength = option.label.length;
                              const isOverLimit = labelLength > maxLabelLength;
                              const isNearLimit = labelLength >= maxLabelLength - 3 && !isOverLimit;

                              return (
                                <div key={option.id} style={styles.optionItem}>
                                  <span style={styles.optionNumber}>{optionIndex + 1}</span>
                                  <input
                                    type="text"
                                    value={option.id}
                                    onChange={(e) => handleUpdateOption(index, optionIndex, 'id', e.target.value)}
                                    onFocus={() => handleFocusStep(index)}
                                    onClick={(e) => e.stopPropagation()}
                                    style={styles.optionIdInput}
                                    placeholder="ID"
                                    maxLength={WHATSAPP_LIMITS.MAX_BUTTON_ID_LENGTH}
                                  />
                                  <div style={styles.optionLabelWrapper}>
                                    <input
                                      type="text"
                                      value={option.label}
                                      onChange={(e) => handleUpdateOption(index, optionIndex, 'label', e.target.value)}
                                      onFocus={() => handleFocusStep(index)}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        ...styles.optionLabelInput,
                                        ...(isOverLimit ? styles.inputError : {}),
                                        ...(isNearLimit ? styles.inputWarning : {}),
                                      }}
                                      placeholder={`Label (max ${maxLabelLength} chars)`}
                                      maxLength={maxLabelLength}
                                    />
                                    <span
                                      style={{
                                        ...styles.charCount,
                                        ...(isOverLimit ? styles.charCountError : {}),
                                        ...(isNearLimit ? styles.charCountWarning : {}),
                                      }}
                                    >
                                      {labelLength}/{maxLabelLength}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveOption(index, optionIndex);
                                    }}
                                    style={styles.optionRemoveButton}
                                    title="Remove option"
                                  >
                                    x
                                  </button>
                                </div>
                              );
                            })}
                            {(step.options?.length || 0) === 0 && (
                              <div style={styles.emptyOptionsHint}>
                                No options added. Click "Add Option" below.
                              </div>
                            )}
                          </div>
                          {(step.options?.length || 0) >= getMaxOptionsForStepType(step.type) ? (
                            <div style={styles.limitReachedHint}>
                              WhatsApp limit reached: max {getMaxOptionsForStepType(step.type)} {step.type === 'buttons' ? 'buttons' : 'list items'}
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddOption(index);
                              }}
                              style={styles.addOptionButton}
                            >
                              + Add Option ({(step.options?.length || 0)}/{getMaxOptionsForStepType(step.type)})
                            </button>
                          )}
                        </div>
                      )}
                      {step.type === 'time_picker' && (() => {
                        // Calculate number of time slots
                        const startHour = step.timePickerConfig?.startHour ?? 6;
                        const endHour = step.timePickerConfig?.endHour ?? 22;
                        const intervalMinutes = step.timePickerConfig?.intervalMinutes ?? 30;
                        const totalMinutes = (endHour - startHour) * 60;
                        const numSlots = Math.floor(totalMinutes / intervalMinutes) + 1;
                        const exceedsLimit = numSlots > WHATSAPP_LIMITS.MAX_LIST_ITEMS;

                        return (
                          <div style={styles.configSection}>
                            <div style={styles.configSectionHeader}>
                              <label style={styles.configSectionLabel}>Time Picker Configuration</label>
                              <span style={{
                                ...styles.limitIndicator,
                                ...(exceedsLimit ? styles.limitIndicatorError : {}),
                              }}>
                                {numSlots}/{WHATSAPP_LIMITS.MAX_LIST_ITEMS} slots max
                              </span>
                            </div>
                            {exceedsLimit && (
                              <div style={styles.warningMessage}>
                                WhatsApp only supports {WHATSAPP_LIMITS.MAX_LIST_ITEMS} time slots. Current config generates {numSlots} slots. Increase the interval or reduce the time range.
                              </div>
                            )}
                            <div style={styles.configRow}>
                              <div style={styles.configField}>
                                <label style={styles.configFieldLabel}>Start Hour</label>
                                <input
                                  type="number"
                                  min={0}
                                  max={23}
                                  value={step.timePickerConfig?.startHour ?? 6}
                                  onChange={(e) => handleUpdateTimePickerConfig(index, 'startHour', parseInt(e.target.value) || 0)}
                                  onFocus={() => handleFocusStep(index)}
                                  onClick={(e) => e.stopPropagation()}
                                  style={styles.configInputSmall}
                                />
                              </div>
                              <div style={styles.configField}>
                                <label style={styles.configFieldLabel}>End Hour</label>
                                <input
                                  type="number"
                                  min={0}
                                  max={23}
                                  value={step.timePickerConfig?.endHour ?? 22}
                                  onChange={(e) => handleUpdateTimePickerConfig(index, 'endHour', parseInt(e.target.value) || 0)}
                                  onFocus={() => handleFocusStep(index)}
                                  onClick={(e) => e.stopPropagation()}
                                  style={styles.configInputSmall}
                                />
                              </div>
                              <div style={styles.configField}>
                                <label style={styles.configFieldLabel}>Interval (min)</label>
                                <input
                                  type="number"
                                  min={5}
                                  max={120}
                                  step={5}
                                  value={step.timePickerConfig?.intervalMinutes ?? 30}
                                  onChange={(e) => handleUpdateTimePickerConfig(index, 'intervalMinutes', parseInt(e.target.value) || 30)}
                                  onFocus={() => handleFocusStep(index)}
                                  onClick={(e) => e.stopPropagation()}
                                  style={styles.configInputSmall}
                                />
                              </div>
                              <div style={styles.configField}>
                                <label style={styles.configFieldLabel}>Format</label>
                                <select
                                  value={step.timePickerConfig?.format || '12h'}
                                  onChange={(e) => handleUpdateTimePickerConfig(index, 'format', e.target.value)}
                                  onFocus={() => handleFocusStep(index)}
                                  onClick={(e) => e.stopPropagation()}
                                  style={styles.configSelect}
                                >
                                  <option value="12h">12-hour (AM/PM)</option>
                                  <option value="24h">24-hour</option>
                                </select>
                              </div>
                            </div>
                            <span style={styles.configHint}>
                              Time slots from {step.timePickerConfig?.startHour ?? 6}:00 to {step.timePickerConfig?.endHour ?? 22}:00, every {step.timePickerConfig?.intervalMinutes ?? 30} minutes
                            </span>
                          </div>
                        );
                      })()}
                      {step.type === 'payment' && (() => {
                        const stripePaymentLink = step.paymentConfig?.stripePaymentLink || '';
                        const ctaButtonText = step.paymentConfig?.ctaButtonText || '';
                        const ctaLength = ctaButtonText.length;
                        const maxCtaLength = WHATSAPP_LIMITS.MAX_BUTTON_LABEL_LENGTH;
                        const isCtaOverLimit = ctaLength > maxCtaLength;
                        const isCtaNearLimit = ctaLength >= maxCtaLength - 3 && !isCtaOverLimit;
                        const headerText = step.paymentConfig?.headerText || '';
                        const footerText = step.paymentConfig?.footerText || '';
                        const isHeaderOverLimit = headerText.length > WHATSAPP_LIMITS.MAX_HEADER_TEXT_LENGTH;
                        const isFooterOverLimit = footerText.length > WHATSAPP_LIMITS.MAX_FOOTER_TEXT_LENGTH;

                        return (
                          <div style={styles.configSection}>
                            <label style={styles.configSectionLabel}>Stripe Payment Link Configuration</label>
                            <div style={styles.stripeInfoBanner}>
                              <span style={styles.stripeInfoIcon}>💳</span>
                              <span>Users will be directed to your Stripe payment page to complete their subscription.</span>
                            </div>
                            <div style={styles.configField}>
                              <label style={styles.configFieldLabel}>Stripe Payment Link URL *</label>
                              <input
                                type="url"
                                value={stripePaymentLink}
                                onChange={(e) => handleUpdatePaymentConfigField(index, 'stripePaymentLink', e.target.value)}
                                onFocus={() => handleFocusStep(index)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  ...styles.configInput,
                                  ...(!stripePaymentLink ? styles.inputWarning : {}),
                                }}
                                placeholder="https://buy.stripe.com/..."
                              />
                              <span style={styles.configHint}>
                                Create a payment link in your Stripe Dashboard and paste it here
                              </span>
                            </div>
                            <div style={styles.configField}>
                              <div style={styles.configSectionHeader}>
                                <label style={styles.configFieldLabel}>Button Text</label>
                                <span
                                  style={{
                                    ...styles.charCount,
                                    ...(isCtaOverLimit ? styles.charCountError : {}),
                                    ...(isCtaNearLimit ? styles.charCountWarning : {}),
                                  }}
                                >
                                  {ctaLength}/{maxCtaLength}
                                </span>
                              </div>
                              <input
                                type="text"
                                value={ctaButtonText}
                                onChange={(e) => handleUpdatePaymentConfigField(index, 'ctaButtonText', e.target.value)}
                                onFocus={() => handleFocusStep(index)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  ...styles.configInput,
                                  ...(isCtaOverLimit ? styles.inputError : {}),
                                  ...(isCtaNearLimit ? styles.inputWarning : {}),
                                }}
                                placeholder="Subscribe Now"
                                maxLength={maxCtaLength}
                              />
                            </div>
                            <div style={styles.configField}>
                              <div style={styles.configSectionHeader}>
                                <label style={styles.configFieldLabel}>Header Text (optional)</label>
                                <span
                                  style={{
                                    ...styles.charCount,
                                    ...(isHeaderOverLimit ? styles.charCountError : {}),
                                  }}
                                >
                                  {headerText.length}/{WHATSAPP_LIMITS.MAX_HEADER_TEXT_LENGTH}
                                </span>
                              </div>
                              <input
                                type="text"
                                value={headerText}
                                onChange={(e) => handleUpdatePaymentConfigText(index, 'headerText', e.target.value)}
                                onFocus={() => handleFocusStep(index)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  ...styles.configInput,
                                  ...(isHeaderOverLimit ? styles.inputError : {}),
                                }}
                                placeholder="e.g., Premium Subscription"
                                maxLength={WHATSAPP_LIMITS.MAX_HEADER_TEXT_LENGTH}
                              />
                            </div>
                            <div style={styles.configField}>
                              <div style={styles.configSectionHeader}>
                                <label style={styles.configFieldLabel}>Footer Text (optional)</label>
                                <span
                                  style={{
                                    ...styles.charCount,
                                    ...(isFooterOverLimit ? styles.charCountError : {}),
                                  }}
                                >
                                  {footerText.length}/{WHATSAPP_LIMITS.MAX_FOOTER_TEXT_LENGTH}
                                </span>
                              </div>
                              <input
                                type="text"
                                value={footerText}
                                onChange={(e) => handleUpdatePaymentConfigText(index, 'footerText', e.target.value)}
                                onFocus={() => handleFocusStep(index)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  ...styles.configInput,
                                  ...(isFooterOverLimit ? styles.inputError : {}),
                                }}
                                placeholder="e.g., Cancel anytime"
                                maxLength={WHATSAPP_LIMITS.MAX_FOOTER_TEXT_LENGTH}
                              />
                            </div>
                          </div>
                        );
                      })()}
                      {step.type === 'informational' && (() => {
                        const continueText = step.continueButtonText || '';
                        const textLength = continueText.length;
                        const maxLength = WHATSAPP_LIMITS.MAX_CONTINUE_BUTTON_LENGTH;
                        const isOverLimit = textLength > maxLength;
                        const isNearLimit = textLength >= maxLength - 3 && !isOverLimit;

                        return (
                          <div style={styles.continueButtonField}>
                            <div style={styles.configSectionHeader}>
                              <label style={styles.continueButtonLabel}>Continue Button Text</label>
                              <span
                                style={{
                                  ...styles.charCount,
                                  ...(isOverLimit ? styles.charCountError : {}),
                                  ...(isNearLimit ? styles.charCountWarning : {}),
                                }}
                              >
                                {textLength}/{maxLength}
                              </span>
                            </div>
                            <input
                              type="text"
                              value={continueText}
                              onChange={(e) => handleUpdateStep(index, 'continueButtonText', e.target.value)}
                              onFocus={() => handleFocusStep(index)}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                ...styles.continueButtonInput,
                                ...(isOverLimit ? styles.inputError : {}),
                                ...(isNearLimit ? styles.inputWarning : {}),
                              }}
                              placeholder="e.g., Got it!, Continue, Next..."
                              maxLength={maxLength}
                            />
                            <span style={styles.continueButtonHint}>
                              Text displayed on the continue button (max {maxLength} characters for WhatsApp)
                            </span>
                          </div>
                        );
                      })()}
                      {/* Date Picker Configuration - Desktop Only */}
                      {step.type === 'date-picker' && activePlatform === 'desktop' && (
                        <div style={styles.configSection}>
                          <label style={styles.configSectionLabel}>Date Picker Configuration</label>
                          <div style={styles.configRow}>
                            <div style={styles.configField}>
                              <label style={styles.configFieldLabel}>Min Date</label>
                              <input
                                type="date"
                                value={step.datePickerConfig?.minDate || ''}
                                onChange={(e) => handleUpdateDatePickerConfig(index, 'minDate', e.target.value)}
                                onFocus={() => handleFocusStep(index)}
                                onClick={(e) => e.stopPropagation()}
                                style={styles.configInput}
                              />
                            </div>
                            <div style={styles.configField}>
                              <label style={styles.configFieldLabel}>Max Date</label>
                              <input
                                type="date"
                                value={step.datePickerConfig?.maxDate || ''}
                                onChange={(e) => handleUpdateDatePickerConfig(index, 'maxDate', e.target.value)}
                                onFocus={() => handleFocusStep(index)}
                                onClick={(e) => e.stopPropagation()}
                                style={styles.configInput}
                              />
                            </div>
                          </div>
                          <div style={styles.configRow}>
                            <div style={styles.configField}>
                              <label style={styles.configFieldLabel}>Date Format</label>
                              <select
                                value={step.datePickerConfig?.format || 'MM/DD/YYYY'}
                                onChange={(e) => handleUpdateDatePickerConfig(index, 'format', e.target.value)}
                                onFocus={() => handleFocusStep(index)}
                                onClick={(e) => e.stopPropagation()}
                                style={styles.configSelect}
                              >
                                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                              </select>
                            </div>
                            <div style={styles.configField}>
                              <label style={styles.configFieldLabel}>Include Time</label>
                              <select
                                value={step.datePickerConfig?.showTime ? 'yes' : 'no'}
                                onChange={(e) => handleUpdateDatePickerConfig(index, 'showTime', e.target.value === 'yes')}
                                onFocus={() => handleFocusStep(index)}
                                onClick={(e) => e.stopPropagation()}
                                style={styles.configSelect}
                              >
                                <option value="no">Date Only</option>
                                <option value="yes">Date & Time</option>
                              </select>
                            </div>
                          </div>
                          <span style={styles.configHint}>
                            Configure date selection constraints and display format
                          </span>
                        </div>
                      )}
                      {/* Dropdown Configuration - Desktop Only */}
                      {step.type === 'dropdown' && activePlatform === 'desktop' && (
                        <div style={styles.configSection}>
                          <div style={styles.configSectionHeader}>
                            <label style={styles.configSectionLabel}>Dropdown Options</label>
                            <span style={styles.limitIndicator}>
                              {step.options?.length || 0} options
                            </span>
                          </div>
                          <div style={styles.optionsList}>
                            {(step.options || []).map((option, optionIndex) => (
                              <div key={option.id} style={styles.optionItem}>
                                <span style={styles.optionNumber}>{optionIndex + 1}</span>
                                <input
                                  type="text"
                                  value={option.id}
                                  onChange={(e) => handleUpdateOption(index, optionIndex, 'id', e.target.value)}
                                  onFocus={() => handleFocusStep(index)}
                                  onClick={(e) => e.stopPropagation()}
                                  style={styles.optionIdInput}
                                  placeholder="ID"
                                />
                                <div style={styles.optionLabelWrapper}>
                                  <input
                                    type="text"
                                    value={option.label}
                                    onChange={(e) => handleUpdateOption(index, optionIndex, 'label', e.target.value)}
                                    onFocus={() => handleFocusStep(index)}
                                    onClick={(e) => e.stopPropagation()}
                                    style={styles.optionLabelInput}
                                    placeholder="Label"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveOption(index, optionIndex);
                                  }}
                                  style={styles.optionRemoveButton}
                                  title="Remove option"
                                >
                                  x
                                </button>
                              </div>
                            ))}
                            {(step.options?.length || 0) === 0 && (
                              <div style={styles.emptyOptionsHint}>
                                No options added. Click "Add Option" below.
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddOption(index);
                            }}
                            style={styles.addOptionButton}
                          >
                            + Add Option
                          </button>
                          <div style={styles.configRow}>
                            <div style={styles.configField}>
                              <label style={styles.configFieldLabel}>Placeholder</label>
                              <input
                                type="text"
                                value={step.dropdownConfig?.placeholder || ''}
                                onChange={(e) => handleUpdateDropdownConfig(index, 'placeholder', e.target.value)}
                                onFocus={() => handleFocusStep(index)}
                                onClick={(e) => e.stopPropagation()}
                                style={styles.configInput}
                                placeholder="Select an option..."
                              />
                            </div>
                            <div style={styles.configField}>
                              <label style={styles.configFieldLabel}>Searchable</label>
                              <select
                                value={step.dropdownConfig?.searchable ? 'yes' : 'no'}
                                onChange={(e) => handleUpdateDropdownConfig(index, 'searchable', e.target.value === 'yes')}
                                onFocus={() => handleFocusStep(index)}
                                onClick={(e) => e.stopPropagation()}
                                style={styles.configSelect}
                              >
                                <option value="no">No</option>
                                <option value="yes">Yes</option>
                              </select>
                            </div>
                          </div>
                          <span style={styles.configHint}>
                            Configure dropdown options and search functionality
                          </span>
                        </div>
                      )}
                      {/* Multi-Select Configuration - Desktop Only */}
                      {step.type === 'multi-select' && activePlatform === 'desktop' && (
                        <div style={styles.configSection}>
                          <div style={styles.configSectionHeader}>
                            <label style={styles.configSectionLabel}>Multi-Select Options</label>
                            <span style={styles.limitIndicator}>
                              {step.options?.length || 0} options
                            </span>
                          </div>
                          {/* Options editor - reuses the same pattern as buttons/list */}
                          <div style={styles.optionsList}>
                            {(step.options || []).map((option, optionIndex) => (
                              <div key={option.id} style={styles.optionItem}>
                                <span style={styles.optionNumber}>{optionIndex + 1}</span>
                                <input
                                  type="text"
                                  value={option.id}
                                  onChange={(e) => handleUpdateOption(index, optionIndex, 'id', e.target.value)}
                                  onFocus={() => handleFocusStep(index)}
                                  onClick={(e) => e.stopPropagation()}
                                  style={styles.optionIdInput}
                                  placeholder="ID"
                                />
                                <div style={styles.optionLabelWrapper}>
                                  <input
                                    type="text"
                                    value={option.label}
                                    onChange={(e) => handleUpdateOption(index, optionIndex, 'label', e.target.value)}
                                    onFocus={() => handleFocusStep(index)}
                                    onClick={(e) => e.stopPropagation()}
                                    style={styles.optionLabelInput}
                                    placeholder="Label"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveOption(index, optionIndex);
                                  }}
                                  style={styles.optionRemoveButton}
                                  title="Remove option"
                                >
                                  x
                                </button>
                              </div>
                            ))}
                            {(step.options?.length || 0) === 0 && (
                              <div style={styles.emptyOptionsHint}>
                                No options added. Click "Add Option" below.
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddOption(index);
                            }}
                            style={styles.addOptionButton}
                          >
                            + Add Option
                          </button>
                          {/* Multi-select specific configuration */}
                          <div style={{ ...styles.configRow, marginTop: '16px' }}>
                            <div style={styles.configField}>
                              <label style={styles.configFieldLabel}>Min Selections</label>
                              <input
                                type="number"
                                min={0}
                                value={step.multiSelectConfig?.minSelections ?? ''}
                                onChange={(e) => handleUpdateMultiSelectConfig(index, 'minSelections', e.target.value ? parseInt(e.target.value) : undefined)}
                                onFocus={() => handleFocusStep(index)}
                                onClick={(e) => e.stopPropagation()}
                                style={styles.configInputSmall}
                                placeholder="0"
                              />
                            </div>
                            <div style={styles.configField}>
                              <label style={styles.configFieldLabel}>Max Selections</label>
                              <input
                                type="number"
                                min={1}
                                value={step.multiSelectConfig?.maxSelections ?? ''}
                                onChange={(e) => handleUpdateMultiSelectConfig(index, 'maxSelections', e.target.value ? parseInt(e.target.value) : undefined)}
                                onFocus={() => handleFocusStep(index)}
                                onClick={(e) => e.stopPropagation()}
                                style={styles.configInputSmall}
                                placeholder="No limit"
                              />
                            </div>
                            <div style={styles.configField}>
                              <label style={styles.configFieldLabel}>Layout</label>
                              <select
                                value={step.multiSelectConfig?.layout || 'list'}
                                onChange={(e) => handleUpdateMultiSelectConfig(index, 'layout', e.target.value as 'grid' | 'list' | 'chips')}
                                onFocus={() => handleFocusStep(index)}
                                onClick={(e) => e.stopPropagation()}
                                style={styles.configSelect}
                              >
                                <option value="list">List</option>
                                <option value="grid">Grid</option>
                                <option value="chips">Chips</option>
                              </select>
                            </div>
                          </div>
                          <span style={styles.configHint}>
                            Configure selection limits and display layout for multi-select options
                          </span>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              <button
                type="button"
                onClick={handleAddStep}
                style={styles.addStepButton}
              >
                + Add Step
              </button>

              {/* Completion Message */}
              <div
                style={{
                  ...styles.completionSection,
                  ...(isCompletionFocused ? styles.completionSectionFocused : {}),
                }}
                onClick={handleFocusCompletion}
              >
                <h3 style={styles.completionTitle}>Completion Message</h3>
                <textarea
                  value={currentCompletionMessage}
                  onChange={(e) => handleUpdateCompletionMessage(e.target.value)}
                  onFocus={handleFocusCompletion}
                  onClick={(e) => e.stopPropagation()}
                  style={styles.completionTextarea}
                  rows={3}
                  placeholder="Message shown after onboarding is complete..."
                />
                <span style={styles.completionHint}>
                  This message is displayed to users after they complete all onboarding steps
                </span>
              </div>

              {activePlatform === 'messaging' && (
                <div style={styles.configSection}>
                  <label style={styles.externalOnboardingToggle}>
                    <input
                      type="checkbox"
                      checked={externalOnboardingEnabled}
                      onChange={(e) => handleUpdateRequireExternalOnboarding(e.target.checked)}
                    />
                    <span style={styles.configSectionLabel}>Require external onboarding</span>
                  </label>
                  <div style={styles.configField}>
                    <label style={styles.configFieldLabel}>External onboarding message</label>
                    <textarea
                      value={messagingFormState.onboardingFlow.externalOnboardingMessage ?? ''}
                      onChange={(e) => handleUpdateExternalOnboardingMessage(e.target.value)}
                      maxLength={2000}
                      disabled={!externalOnboardingEnabled}
                      style={{
                        ...styles.completionTextarea,
                        ...(!externalOnboardingEnabled ? styles.disabledTextarea : {}),
                        ...(externalOnboardingError ? styles.textareaError : {}),
                      }}
                      rows={4}
                      placeholder={externalOnboardingEnabled
                        ? 'Message shown to direct WhatsApp users who must finish onboarding elsewhere first...'
                        : 'Enable external onboarding to edit the blocked-access message...'}
                    />
                    <span style={styles.configHint}>
                      {externalOnboardingEnabled
                        ? 'Sent instead of WhatsApp onboarding or bot replies when direct access is blocked.'
                        : 'Stored text is kept as an inactive draft until external onboarding is enabled.'}
                    </span>
                    {externalOnboardingError && (
                      <span style={styles.fieldErrorText}>{externalOnboardingError}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={styles.actions}>
                <button
                  type="button"
                  onClick={() => navigate('/bot/details')}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending || !hasChanges}
                  style={{
                    ...styles.submitButton,
                    ...(updateMutation.isPending || !hasChanges ? styles.buttonDisabled : {}),
                  }}
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Right Pane - Preview */}
        <div style={styles.previewPane}>
          <div style={styles.previewContainer}>
            <div style={styles.previewHeader}>
              <h3 style={styles.previewTitle}>
                {activePlatform === 'messaging' ? 'Mobile Preview' : 'Desktop Preview'}
              </h3>
              <span style={styles.previewPlatformBadge}>
                {activePlatform === 'messaging' ? '📱 WhatsApp/Telegram' : '🖥️ Web App'}
              </span>
              <span style={styles.previewHint}>
                {focusedStep
                  ? `Previewing Step ${focusedStepIndex! + 1}`
                  : isCompletionFocused
                    ? 'Previewing Completion'
                    : 'Click a step to preview'}
              </span>
            </div>
            <OnboardingStepPreview
              step={focusedStep as OnboardingStep | null}
              stepNumber={focusedStepIndex !== null ? focusedStepIndex + 1 : undefined}
              totalSteps={currentSteps.length}
              isCompletionMessage={isCompletionFocused}
              completionMessage={currentCompletionMessage}
              platform={activePlatform === 'desktop' ? 'desktop' : 'chat'}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxWidth: '1400px',
  },
  header: {
    marginBottom: '24px',
    flexShrink: 0,
  },
  splitPane: {
    display: 'flex',
    gap: '24px',
    flex: 1,
    minHeight: 0,
  },
  editorPane: {
    flex: 1,
    minWidth: 0,
    overflowY: 'auto',
  },
  previewPane: {
    width: '380px',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    alignSelf: 'flex-start',
  },
  previewContainer: {
    backgroundColor: '#f8fafc',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid #e2e8f0',
  },
  previewHeader: {
    marginBottom: '16px',
    textAlign: 'center',
  },
  previewTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '4px',
    margin: 0,
  },
  previewHint: {
    fontSize: '12px',
    color: '#64748b',
    display: 'block',
    marginTop: '4px',
  },
  previewPlatformBadge: {
    display: 'inline-block',
    fontSize: '11px',
    color: '#475569',
    backgroundColor: '#e2e8f0',
    padding: '2px 8px',
    borderRadius: '12px',
    marginTop: '8px',
  },
  // Platform toggle styles
  platformToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #e2e8f0',
  },
  platformTabs: {
    display: 'flex',
    gap: '8px',
  },
  platformTab: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#64748b',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
  },
  platformTabActive: {
    backgroundColor: '#3b82f6',
    borderColor: '#3b82f6',
    color: '#ffffff',
  },
  platformTabIcon: {
    fontSize: '16px',
  },
  unsavedDot: {
    position: 'absolute',
    top: '6px',
    right: '6px',
    width: '8px',
    height: '8px',
    backgroundColor: '#f59e0b',
    borderRadius: '50%',
  },
  copyFlowContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  copyFlowSelect: {
    padding: '8px 12px',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#475569',
    cursor: 'pointer',
  },
  platformHint: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    backgroundColor: '#fef3c7',
    border: '1px solid #fcd34d',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#92400e',
    marginBottom: '16px',
  },
  platformHintDesktop: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    backgroundColor: '#dbeafe',
    border: '1px solid #93c5fd',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#1e40af',
    marginBottom: '16px',
  },
  platformHintIcon: {
    fontSize: '16px',
    flexShrink: 0,
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#3b82f6',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0,
  },
  botName: {
    fontSize: '14px',
    color: '#64748b',
    backgroundColor: '#f1f5f9',
    padding: '4px 12px',
    borderRadius: '16px',
  },
  saveButton: {
    padding: '10px 24px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#64748b',
  },
  error: {
    padding: '16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
  },
  form: {
    width: '100%',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '8px',
  },
  cardDescription: {
    fontSize: '14px',
    color: '#64748b',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  errorMessage: {
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  successMessage: {
    padding: '12px 16px',
    backgroundColor: '#f0fdf4',
    color: '#16a34a',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  stepsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '20px',
  },
  emptyState: {
    padding: '40px 20px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '2px dashed #e2e8f0',
    textAlign: 'center',
  },
  emptyStateIcon: {
    fontSize: '40px',
    marginBottom: '12px',
  },
  emptyStateText: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#475569',
    marginBottom: '8px',
  },
  emptyStateHint: {
    fontSize: '14px',
    color: '#94a3b8',
    margin: 0,
  },
  stepCard: {
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '16px',
    borderLeft: '4px solid #3b82f6',
    cursor: 'grab',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-top 0.15s ease',
  },
  stepCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  stepNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '600',
    flexShrink: 0,
  },
  stepTypeSelect: {
    padding: '8px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
  },
  stepFieldContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#f1f5f9',
    padding: '4px 8px',
    borderRadius: '4px',
  },
  stepFieldLabel: {
    fontSize: '12px',
    color: '#64748b',
    whiteSpace: 'nowrap' as const,
  },
  stepFieldInput: {
    fontSize: '12px',
    fontFamily: 'monospace',
    color: '#334155',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    padding: '4px 8px',
    width: '120px',
    outline: 'none',
  },
  stepActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginLeft: 'auto',
  },
  stepActionButton: {
    padding: '6px 10px',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    color: '#475569',
  },
  stepActionButtonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  stepRemoveButton: {
    padding: '6px 12px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    color: '#dc2626',
  },
  stepMessageInput: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    lineHeight: '1.5',
  },
  // Drag-and-drop styles for onboarding steps
  dragHandle: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#94a3b8',
    fontSize: '16px',
    cursor: 'grab',
    padding: '4px',
    marginRight: '4px',
    userSelect: 'none',
    letterSpacing: '2px',
  },
  stepCardFocused: {
    borderLeftColor: '#22c55e',
    borderLeftWidth: '4px',
    backgroundColor: '#f0fdf4',
    boxShadow: '0 0 0 2px rgba(34, 197, 94, 0.2)',
  },
  stepCardDragging: {
    opacity: 0.5,
    cursor: 'grabbing',
  },
  stepCardDragOver: {
    borderTop: '3px solid #3b82f6',
    marginTop: '-3px',
  },
  // Media URL field styles for media step type
  mediaUrlField: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px dashed #e2e8f0',
  },
  mediaUrlLabel: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#475569',
    marginBottom: '6px',
  },
  mediaUrlInput: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
  },
  mediaUrlHint: {
    display: 'block',
    fontSize: '12px',
    color: '#94a3b8',
    marginTop: '4px',
  },
  // Continue button field styles for informational step type
  continueButtonField: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px dashed #e2e8f0',
  },
  continueButtonLabel: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#475569',
    marginBottom: '6px',
  },
  continueButtonInput: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
  },
  continueButtonHint: {
    display: 'block',
    fontSize: '12px',
    color: '#94a3b8',
    marginTop: '4px',
  },
  // Configuration section styles
  configSection: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px dashed #e2e8f0',
  },
  externalOnboardingToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '16px',
    color: '#475569',
  },
  configSectionLabel: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
    marginBottom: '12px',
  },
  configRow: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    marginBottom: '12px',
  },
  configField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: '1 1 auto',
    minWidth: '120px',
    marginBottom: '8px',
  },
  configFieldLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#64748b',
  },
  textareaError: {
    borderColor: '#dc2626',
    boxShadow: '0 0 0 1px rgba(220, 38, 38, 0.15)',
  },
  disabledTextarea: {
    backgroundColor: '#f8fafc',
    color: '#94a3b8',
    cursor: 'not-allowed',
  },
  fieldErrorText: {
    fontSize: '12px',
    color: '#dc2626',
    marginTop: '2px',
  },
  configInput: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '13px',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
  },
  configInputSmall: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '13px',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    maxWidth: '100px',
  },
  configSelect: {
    padding: '8px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '13px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    minWidth: '120px',
  },
  configHint: {
    display: 'block',
    fontSize: '12px',
    color: '#94a3b8',
    marginTop: '4px',
  },
  // Options list styles (for buttons/list steps)
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '12px',
  },
  optionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#ffffff',
    padding: '8px',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
  },
  optionNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    backgroundColor: '#e2e8f0',
    color: '#475569',
    fontSize: '11px',
    fontWeight: '600',
    flexShrink: 0,
  },
  optionIdInput: {
    flex: '0 0 100px',
    padding: '6px 8px',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'monospace',
    outline: 'none',
    backgroundColor: '#f8fafc',
  },
  optionLabelInput: {
    flex: 1,
    padding: '6px 8px',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    fontSize: '13px',
    outline: 'none',
  },
  optionRemoveButton: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '4px',
    color: '#dc2626',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    flexShrink: 0,
  },
  addOptionButton: {
    padding: '8px 16px',
    backgroundColor: '#f0f9ff',
    border: '1px dashed #93c5fd',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#3b82f6',
    cursor: 'pointer',
    width: '100%',
    marginBottom: '8px',
  },
  emptyOptionsHint: {
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '6px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#94a3b8',
    border: '1px dashed #e2e8f0',
  },
  // Payment plans styles
  plansList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '12px',
  },
  planItem: {
    backgroundColor: '#ffffff',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  planItemHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '1px solid #f1f5f9',
  },
  planItemTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
  },
  planRemoveButton: {
    padding: '4px 10px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '4px',
    fontSize: '11px',
    color: '#dc2626',
    cursor: 'pointer',
  },
  planFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  planFieldRow: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  addStepButton: {
    padding: '12px 24px',
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    color: '#16a34a',
    marginBottom: '24px',
    width: '100%',
  },
  completionSection: {
    marginBottom: '24px',
    paddingTop: '20px',
    borderTop: '1px solid #f1f5f9',
    padding: '16px',
    marginLeft: '-16px',
    marginRight: '-16px',
    cursor: 'pointer',
    borderRadius: '8px',
    transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
  },
  completionSectionFocused: {
    backgroundColor: '#f0fdf4',
    boxShadow: '0 0 0 2px rgba(34, 197, 94, 0.2)',
  },
  completionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '8px',
  },
  completionTextarea: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    lineHeight: '1.5',
  },
  completionHint: {
    display: 'block',
    fontSize: '12px',
    color: '#94a3b8',
    marginTop: '6px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    paddingTop: '20px',
    borderTop: '1px solid #f1f5f9',
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#475569',
    cursor: 'pointer',
  },
  submitButton: {
    padding: '10px 24px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  buttonDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
  },
  // WhatsApp limit indicator and validation styles
  configSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  limitIndicator: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#64748b',
    backgroundColor: '#f1f5f9',
    padding: '2px 8px',
    borderRadius: '12px',
  },
  limitIndicatorError: {
    color: '#dc2626',
    backgroundColor: '#fef2f2',
  },
  optionLabelWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    position: 'relative',
  },
  charCount: {
    fontSize: '10px',
    color: '#94a3b8',
    textAlign: 'right',
    marginTop: '2px',
  },
  charCountWarning: {
    color: '#d97706',
  },
  charCountError: {
    color: '#dc2626',
  },
  inputWarning: {
    borderColor: '#d97706',
    backgroundColor: '#fffbeb',
  },
  inputError: {
    borderColor: '#dc2626',
    backgroundColor: '#fef2f2',
  },
  limitReachedHint: {
    padding: '10px 16px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#dc2626',
    textAlign: 'center',
    marginBottom: '8px',
  },
  warningMessage: {
    padding: '10px 12px',
    backgroundColor: '#fffbeb',
    border: '1px solid #fcd34d',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#92400e',
    marginBottom: '12px',
    lineHeight: '1.4',
  },
  // Stripe payment link styles
  stripeInfoBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: '#f0f9ff',
    border: '1px solid #bae6fd',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '13px',
    color: '#0369a1',
    lineHeight: '1.4',
  },
  stripeInfoIcon: {
    fontSize: '20px',
    flexShrink: 0,
  },
};

export default BotOnboardingEditPage;
